import { enableBodyScroll, disableBodyScroll } from 'body-scroll-lock';
import Plyr from 'plyr';

import AbstractComponent from './AbstractComponent';

/**
 * Manages the solutions video component.
 */
export default class SolutionsVideo extends AbstractComponent {
  /**
   * The HTML elements of the controls in the CTA form.
   */
  private $ctaFormControls: NodeListOf<HTMLInputElement>;

  /**
   * The HTML element of the video button.
   */
  private $videoButton: HTMLButtonElement;

  /**
   * The HTML element of the close button in the dialog.
   */
  private $closeButton: HTMLButtonElement;

  /**
   * The HTML element of the dialog body.
   */
  private $dialogBody: HTMLElement;

  /**
   * The HTML element of the backdrop element.
   */
  private $backdrop: HTMLElement;

  /**
   * The HTML element of the CTA form.
   */
  private $ctaForm: HTMLElement;

  /**
   * The HTML element of the dialog.
   */
  private $dialog: HTMLElement;

  /**
   * The HTML element of the share dialog.
   */
  private $share: HTMLElement;

  /**
   * The HTML element of the video.
   */
  private $video: HTMLElement;

  /**
   * The HTML element of the CTA wrapper.
   */
  private $cta: HTMLElement;

  /**
   * The timeout of when the video should start playing.
   */
  private startVideoTimeout: number;

  /**
   * The video player.
   */
  private videoPlayer: Plyr;

  /**
   * Initializes the solutions video component.
   */
  public initialize(): void {
    this.$ctaFormControls = document.querySelectorAll('.dialog__cta .form__control');
    this.$videoButton = document.querySelector('.solutions__video');
    this.$closeButton = document.querySelector('.dialog__close');
    this.$dialogBody = document.querySelector('.dialog__body');
    this.$backdrop = document.querySelector('.dialog__backdrop');
    this.$ctaForm = document.querySelector('.dialog__cta .form');
    this.$dialog = document.querySelector('.dialog');
    this.$share = document.querySelector('.dialog__cta__share');
    this.$video = document.querySelector('.dialog__video');
    this.$cta = document.querySelector('.dialog__cta');

    if (!window.navigator.share) {
      this.$share.style.display = 'none';
    } else {
      this.$share.addEventListener('click', this.onShareClick);
    }

    this.videoPlayer = new Plyr(this.$video, {
      tooltips: {
        seek: false,
      },
      controls: [ 'play', 'progress', 'fullscreen' ],
    });

    this.$videoButton.addEventListener('click', (event) => {
      this.onVideoButtonClicked(event);
    });

    this.$closeButton.addEventListener('click', (event) => {
      this.onCloseButtonClicked(event);
    });

    this.$backdrop.addEventListener('click', (event) => {
      this.onBackdropClicked(event);
    });

    window.addEventListener('keyup', (event) => {
      this.onWindowKeyUp(event);
    });

    this.videoPlayer.on('ended', () => {
      this.onVideoDone();
    });
  }

  private closeDialog(): void {
    window.clearTimeout(this.startVideoTimeout);

    enableBodyScroll(this.$dialogBody);

    this.$dialog.classList.remove('is-shown');

    this.videoPlayer.stop();
  }

  private onVideoButtonClicked(event: MouseEvent): void {
    event.preventDefault();

    disableBodyScroll(this.$dialogBody);

    this.$dialogBody.style.overflowY = 'hidden';
    this.$video.style.display = 'block';
    this.$cta.style.display = 'none';

    this.$ctaForm.classList.remove('has-success');
    this.$dialog.classList.add('is-shown');

    this.$ctaFormControls.forEach(($control) => {
      $control.value = '';
    });

    this.startVideoTimeout = window.setTimeout(() => {
      this.videoPlayer.play();
    }, 150);
  }

  private onCloseButtonClicked(event: MouseEvent): void {
    event.preventDefault();

    this.closeDialog();
  }

  private onBackdropClicked(event: MouseEvent): void {
    event.preventDefault();

    this.closeDialog();
  }

  private onWindowKeyUp(event: KeyboardEvent): void {
    switch (event.key.toLowerCase()) {
    case 'escape':
    case 'esc':
      this.closeDialog();
      break;

    default:
      // All other key presses are ignored.
      break;
    }
  }

  private async onShareClick(event: MouseEvent): Promise<void> {
    event.preventDefault();

    await window.navigator.share({
      text: 'Hi, I came across this website: https://trackcol.com/solutions. They have different smart trackers available that are all connected to an innovative smart logistic platform. Maybe interesting for us?',
    });
  }

  /**
   * Called when the video has finished playing.
   */
  private onVideoDone(): void {
    if (this.videoPlayer.fullscreen.active) {
      this.videoPlayer.fullscreen.exit();
    }

    this.$dialogBody.style.overflowY = 'scroll';
    this.$video.style.display = 'none';
    this.$cta.style.display = 'flex';
  }
}
